import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  useCreateAdminAssessmentMutation,
  useDeleteAdminAssessmentMutation,
  useGetAdminAssessmentQuery,
  useUpdateAdminAssessmentMutation,
} from "app/services/api/adminAssessmentsApi";
import {
  Control,
  useForm,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { AssessmentModel } from "app/services/api/types/forms/models/AssessmentModel";
import { AssessmentFormSectionModel } from "app/services/api/types/forms/models/AssessmentSectionModel";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useRouter } from "expo-router";
import { AssessmentsPath } from "app/constants/routePaths";
import { useModalContext } from "ui/providers/ModalManager";
import { usePermissionCheck } from "app/services/permissions/usePermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { AssessmentEditRequest } from "app/services/api/types/assessments/requests/AssessmentEditRequest";
import { RootState } from "app/services/store";
import { useSelector } from "react-redux";
import { assessmentSectionToPaginatedSections } from "../utils/utils";
import { QuestionTypeEnum } from "app/services/api/types/forms/enums/QuestionTypeEnum";

export type AssessmentEditFormModel = {
  id?: number;
  can_have_draft_status: boolean;
  has_pages: boolean;
  has_scoring: boolean;
  name: string;
  password_protected: boolean;
  requires_electronic_signature: boolean;
  requires_signoff: boolean;
  tag: string;
  paginatedSections: { id: string; sections: AssessmentFormSectionModel[] }[];
};

export interface AssessmentEditContextValue {
  // data and status
  assessment?: AssessmentModel;
  isFetching: boolean;
  isSaving: boolean;
  isEditing: boolean;
  hasEditingPermission: boolean;
  hasPages: boolean;
  error?: unknown;

  // form
  control: Control<AssessmentEditFormModel>;

  appendPage: () => void;
  removePage: (index: number) => void;

  // save
  saveAssessment: (data: AssessmentEditFormModel) => Promise<void>;
  deleteAssessment: () => Promise<void>;
  handleSubmit: UseFormHandleSubmit<
    AssessmentEditFormModel,
    AssessmentEditFormModel
  >;
  cancel: () => void;

  // form utils
  getValues: UseFormGetValues<AssessmentEditFormModel>;
  setValue: UseFormSetValue<AssessmentEditFormModel>;
}

const AssessmentEditContext = createContext<
  AssessmentEditContextValue | undefined
>(undefined);

export function useAssessmentEdit() {
  const ctx = useContext(AssessmentEditContext);
  if (!ctx)
    throw new Error(
      "useAssessmentEdit must be used within AssessmentEditProvider",
    );
  return ctx;
}

interface ProviderProps {
  id?: number;
  children: React.ReactNode;
  loadingFallback?: React.ReactNode;
}

export function AssessmentEditProvider({
  id,
  children,
  loadingFallback,
}: ProviderProps) {
  const { data, isFetching, isError, error } = useGetAdminAssessmentQuery(
    { id: id! },
    { skip: !id },
  );

  const nextPageId = useRef(1);
  const router = useRouter();
  const [updateAssessmentApi] = useUpdateAdminAssessmentMutation();
  const [createAssessmentApi] = useCreateAdminAssessmentMutation();
  const [deleteAssessmentApi] = useDeleteAdminAssessmentMutation();

  const { showErrorToast, showNewToast } = useDisplayToast();
  const { showConfirmationModal } = useModalContext();
  const { checkPermission } = usePermissionCheck();

  const user = useSelector((state: RootState) => state?.authorisation?.user);

  const isEditing = useMemo(() => !!id, [id]);

  const hasEditingPermission = useMemo(() => {
    return checkPermission(
      isEditing ? PermissionsEnums.UPDATE_FORM : PermissionsEnums.CREATE_FORM,
    );
  }, [id, user?.permissions]);

  const {
    control,
    reset,
    getValues,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<AssessmentEditFormModel>({
    defaultValues: {
      id: undefined,
      name: "",
      tag: "",
      password_protected: false,
      has_scoring: false,
      has_pages: false,
      paginatedSections: [],
    },
  });

  const hasPages = useWatch({ control, name: "has_pages" });

  const processPaginatedSections = (sections: AssessmentFormSectionModel[]) => {
    const flatSections = sections.flat();
    if (hasPages) {
      const resp =
        assessmentSectionToPaginatedSections<AssessmentFormSectionModel>(
          flatSections,
        );
      nextPageId.current = resp.nextPage;
      return resp.paginatedSections;
    } else {
      return [{ id: "main", sections: flatSections }];
    }
  };
  useEffect(() => {
    if (data) {
      const paginatedSections = processPaginatedSections(data.sections ?? []);
      reset({
        id: data.id,
        name: data.name,
        tag: data.tag,
        password_protected: data.password_protected ?? false,
        has_scoring: data.has_scoring ?? false,
        has_pages: data.has_pages ?? false,
        requires_electronic_signature:
          data.requires_electronic_signature ?? false,
        requires_signoff: data.requires_signoff ?? false,
        can_have_draft_status: data.can_have_draft_status ?? false,
        paginatedSections,
      });
    }
  }, [data]);

  useEffect(() => {
    const flatSections = getValues("paginatedSections").reduce((acc, page) => {
      return [...acc, ...page.sections];
    }, [] as AssessmentFormSectionModel[]);
    const sections = processPaginatedSections(flatSections);
    setValue("paginatedSections", sections, { shouldDirty: true });
  }, [hasPages]);

  const cancel = () => {
    router.replace(AssessmentsPath);
  };

  const flattenPaginatedSections = (data: AssessmentEditFormModel) => {
    return data.paginatedSections.reduce((acc, page) => {
      return [...acc, ...page.sections];
    }, [] as AssessmentFormSectionModel[]);
  };

  const countElectronicSignatures = (
    sections: AssessmentFormSectionModel[],
  ) => {
    let electronicSignatureNumber = 0;
    for (const section of sections) {
      for (const question of section.questions) {
        if (
          question.type === QuestionTypeEnum.ELECTRONIC_SIGNATURE &&
          question.required
        ) {
          electronicSignatureNumber++;
        }
      }
    }
    return electronicSignatureNumber;
  };

  async function saveAssessment(data: AssessmentEditFormModel) {
    try {
      const sections = flattenPaginatedSections(data);

      let electronicSignatureNumber = 0;
      if (data.requires_electronic_signature) {
        electronicSignatureNumber = countElectronicSignatures(sections);
      }

      const request: AssessmentEditRequest = {
        name: data.name,
        tag: data.tag,
        password_protected: data.password_protected ?? false,
        has_scoring: data.has_scoring ?? false,
        has_pages: data.has_pages ?? false,
        electronic_signatures_nr: electronicSignatureNumber,
        requires_electronic_signature:
          data.requires_electronic_signature ?? false,
        requires_signoff: data.requires_signoff ?? false,
        can_have_draft_status: data.can_have_draft_status ?? false,
        sections,
      };

      if (id) {
        await updateAssessmentApi({ id, form: request }).unwrap();
      } else {
        await createAssessmentApi({ form: request }).unwrap();
      }

      cancel();
      showNewToast({
        title: "Assessment saved",
        message: `${data.name} assessment was ${id ? "updated" : "created"} successfully`,
        type: "success",
      });
    } catch (e) {
      showErrorToast(e);
    }
  }

  async function deleteAssessment() {
    try {
      if (!id) return;
      const confirmed = await showConfirmationModal({
        title: "Are you sure you want to delete this assessment?",
        message:
          "This action will remove this assessment permanently and cannot be undone. Please confirm if you want to proceed.",
      });

      if (!confirmed) return;

      await deleteAssessmentApi({ id }).unwrap();
      cancel();
      showNewToast({
        title: "Assessment deleted",
        message: `${data?.name} assessment was deleted successfully`,
        type: "success",
      });
    } catch (e) {
      showErrorToast(e);
    }
  }

  // inside AssessmentEditProvider (has access to control/getValues/setValue)
  function getPages() {
    return getValues("paginatedSections") ?? [];
  }

  function setPages(
    next: { id: string; sections: AssessmentFormSectionModel[] }[],
  ) {
    // ensure page_nr consistency for all sections
    const normalized = next.map((sections, pIdx) => ({
      id: sections.id,
      sections: sections.sections.map((s) => ({ ...s, page_nr: pIdx + 1 })),
    }));
    setValue("paginatedSections", normalized, { shouldDirty: true });
  }

  const appendPage = useCallback(() => {
    const pages = getPages();
    setPages([...pages, { id: `${nextPageId.current++}`, sections: [] }]);
  }, [getPages, setPages, nextPageId]);

  const removePage = useCallback(
    async (pageIndex: number) => {
      const pages = getPages();
      const confirmed =
        !pages[pageIndex]?.sections.length ||
        (await showConfirmationModal({
          title: "Are you sure you want to delete this page?",
          message:
            "This action will remove this page permanently and cannot be undone. Please confirm if you want to proceed.",
        }));
      if (!confirmed) return;
      if (pageIndex < 0 || pageIndex >= pages.length) return;
      pages.splice(pageIndex, 1);
      setPages(pages);
    },
    [getPages, setPages],
  );

  const value: AssessmentEditContextValue = useMemo(
    () => ({
      assessment: data,
      isFetching,
      isEditing,
      hasEditingPermission,
      hasPages,
      isSaving: isSubmitting,
      error: isError ? error : undefined,
      control,
      appendPage,
      removePage,
      saveAssessment,
      handleSubmit,
      cancel,
      deleteAssessment,
      getValues,
      setValue,
    }),
    [
      data,
      isFetching,
      isEditing,
      hasEditingPermission,
      isSubmitting,
      hasPages,
      isError,
      error,
      control,
      appendPage,
      removePage,
      saveAssessment,
      handleSubmit,
      cancel,
      deleteAssessment,
      getValues,
      setValue,
    ],
  );

  return (
    <AssessmentEditContext.Provider value={value}>
      {isFetching ? loadingFallback : children}
    </AssessmentEditContext.Provider>
  );
}
